import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AIService } from "@/gen/omnios/v1/ai_connect";
import { StoreService } from "@/gen/omnios/v1/store_connect";
import type { PortalInstance } from "@/gen/omnios/v1/store_pb";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	ArrowRight,
	Check,
	Code,
	ExternalLink,
	Globe,
	Layout,
	Plus,
	Rocket,
	ShoppingBag,
	type Store,
	Zap,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/portals/")({
	component: PortalsIndex,
});

const storeClient = createClient(StoreService, transport);
const aiClient = createClient(AIService, transport);

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:50051";

function getPreviewUrl(sessionId: string): string {
	try {
		const url = new URL(API_BASE);
		const port = Number.parseInt(url.port || "50051", 10) + 60;
		return `${url.protocol}//${url.hostname}:${port}/preview/${sessionId}/`;
	} catch {
		return `http://localhost:50111/preview/${sessionId}/`;
	}
}

const STATUS_CONFIG: Record<
	string,
	{ label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
	draft: {
		label: "status_draft",
		variant: "secondary",
	},
	building: {
		label: "status_building",
		variant: "outline",
	},
	preview: {
		label: "status_preview",
		variant: "outline",
	},
	published: {
		label: "status_published",
		variant: "default",
	},
	archived: {
		label: "status_archived",
		variant: "secondary",
	},
};

const TEMPLATE_ICONS: Record<string, typeof Store> = {
	store: ShoppingBag,
	landing: Layout,
	code: Code,
	default: Globe,
};

// ── Main Page ──

function PortalsIndex() {
	const { t } = useTranslation("portals");
	useBreadcrumbs([{ label: t("title") }]);
	const navigate = useNavigate();
	const qc = useQueryClient();
	const [createStep, setCreateStep] = useState<
		"closed" | "template" | "details"
	>("closed");
	const [selectedTemplate, setSelectedTemplate] = useState<string>("");

	const { data: portals, isLoading } = useQuery({
		queryKey: ["portals"],
		queryFn: async () => {
			const res = await storeClient.listPortalInstances({});
			return res.portalInstances;
		},
	})

	const { data: templates } = useQuery({
		queryKey: ["code-templates"],
		queryFn: async () => {
			const res = await aiClient.listCodeTemplates({});
			return res.templates;
		},
	})

	const published = portals?.filter((s: PortalInstance) => s.status === "published") ?? [];
	const inProgress =
		portals?.filter(
			(s: PortalInstance) => s.status === "building" || s.status === "preview",
		) ?? [];
	const drafts = portals?.filter((s: PortalInstance) => s.status === "draft") ?? [];

	return (
		<div className="max-w-6xl mx-auto p-6 h-full overflow-auto">
			{/* Header */}
			<div className="flex items-center justify-between mb-8">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">{t("title")}</h1>
					<p className="text-muted-foreground mt-1">{t("subtitle")}</p>
				</div>
				<Button
					onClick={() => setCreateStep("template")}
					size="lg"
					className="gap-2 shadow-sm"
				>
					<Plus className="h-4 w-4" />
					{t("create_site")}
				</Button>
			</div>

			{isLoading ? (
				<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
					{Array.from({ length: 3 }).map((_, i) => (
						<div
							key={i}
							className="rounded-xl border bg-card animate-pulse h-[380px]"
						/>
					))}
				</div>
			) : !portals?.length ? (
				<EmptyState
					templates={templates ?? []}
					onSelectTemplate={(tplId) => {
						setSelectedTemplate(tplId);
						setCreateStep("details");
					}}
					onBlank={() => setCreateStep("details")}
				/>
			) : (
				<div className="space-y-8">
					{/* Published */}
					{published.length > 0 && (
						<Section title={t("section_published")} count={published.length}>
							{published.map((sf: PortalInstance, i: number) => (
								<PortalCard
									key={sf.id}
									portal={sf}
									navigate={navigate}
									index={i}
								/>
							))}
						</Section>
					)}

					{/* In progress */}
					{inProgress.length > 0 && (
						<Section title={t("section_in_progress")} count={inProgress.length}>
							{inProgress.map((sf: PortalInstance, i: number) => (
								<PortalCard
									key={sf.id}
									portal={sf}
									navigate={navigate}
									index={i}
								/>
							))}
						</Section>
					)}

					{/* Drafts */}
					{drafts.length > 0 && (
						<Section title={t("section_drafts")} count={drafts.length}>
							{drafts.map((sf: PortalInstance, i: number) => (
								<PortalCard
									key={sf.id}
									portal={sf}
									navigate={navigate}
									index={i}
								/>
							))}
						</Section>
					)}
				</div>
			)}

			{/* Create Flow: Step 1 — Template Selection */}
			<Dialog
				open={createStep === "template"}
				onOpenChange={(v) => !v && setCreateStep("closed")}
			>
				<DialogContent className="max-w-lg">
					<DialogHeader>
						<DialogTitle className="text-xl">
							{t("select_template")}
						</DialogTitle>
					</DialogHeader>
					<TemplateGallery
						templates={templates ?? []}
						onSelect={(tplId) => {
							setSelectedTemplate(tplId);
							setCreateStep("details");
						}}
						onBlank={() => {
							setSelectedTemplate("");
							setCreateStep("details");
						}}
					/>
				</DialogContent>
			</Dialog>

			{/* Create Flow: Step 2 — Details */}
			<CreateDetailsDialog
				open={createStep === "details"}
				templateId={selectedTemplate}
				templateName={templates?.find((t) => t.id === selectedTemplate)?.name}
				onBack={() => setCreateStep(templates?.length ? "template" : "closed")}
				onClose={() => setCreateStep("closed")}
				onCreated={(id) => {
					qc.invalidateQueries({ queryKey: ["portals"] });
					setCreateStep("closed");
					toast.success(t("portal_created"));
					navigate({
						to: "/portals/$id/build",
						params: { id },
					})
				}}
			/>
		</div>
	)
}

// ── Section ──

function Section({
	title,
	count,
	children,
}: { title: string; count: number; children: React.ReactNode }) {
	return (
		<div>
			<div className="flex items-center gap-2 mb-4">
				<h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
					{title}
				</h2>
				<Badge variant="secondary" className="text-[10px] px-1.5 py-0">
					{count}
				</Badge>
			</div>
			<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
				{children}
			</div>
		</div>
	)
}

// ── Empty State ──

function EmptyState({
	templates,
	onSelectTemplate,
	onBlank,
}: {
	templates: { id: string; name: string; description: string; icon: string }[];
	onSelectTemplate: (id: string) => void;
	onBlank: () => void;
}) {
	const { t } = useTranslation("portals");
	return (
		<div className="space-y-6">
			{templates.length > 0 ? (
				<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
					{templates.map((tmpl) => (
						<TemplateCard
							key={tmpl.id}
							template={tmpl}
							onSelect={() => onSelectTemplate(tmpl.id)}
						/>
					))}
					<Button
						variant="outline"
						className="flex items-center gap-4 rounded-xl border-2 border-dashed p-5 text-left h-auto hover:border-primary/50 hover:bg-muted/50 min-h-[88px] justify-start"
						onClick={onBlank}
					>
						<div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted shrink-0">
							<Code className="h-6 w-6 text-muted-foreground" />
						</div>
						<div className="flex-1 min-w-0">
							<p className="font-medium">{t("blank_project")}</p>
							<p className="text-xs text-muted-foreground mt-0.5">
								{t("start_from_scratch")}
							</p>
						</div>
					</Button>
				</div>
			) : (
				<Button
					variant="outline"
					className="w-full flex items-center gap-4 rounded-xl border-2 border-dashed p-8 text-left h-auto hover:border-primary/50 hover:bg-muted/50 justify-start"
					onClick={onBlank}
				>
					<div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted shrink-0">
						<Plus className="h-6 w-6 text-muted-foreground" />
					</div>
					<div>
						<p className="font-medium">{t("create_site")}</p>
						<p className="text-xs text-muted-foreground mt-0.5">
							{t("agent_help_from_scratch")}
						</p>
					</div>
				</Button>
			)}
		</div>
	)
}

// ── Template Gallery (in dialog) ──

function TemplateGallery({
	templates,
	onSelect,
	onBlank,
}: {
	templates: {
		id: string;
		name: string;
		description: string;
		icon: string;
		tags: string[];
	}[];
	onSelect: (id: string) => void;
	onBlank: () => void;
}) {
	const { t } = useTranslation("portals");
	return (
		<div className="space-y-4">
			<div className="grid grid-cols-1 gap-3">
				{templates.map((tmpl) => (
					<TemplateCard
						key={tmpl.id}
						template={tmpl}
						onSelect={() => onSelect(tmpl.id)}
						showTags
					/>
				))}

				{/* Blank project card */}
				<Button
					variant="outline"
					className="flex items-center gap-4 rounded-xl border-2 border-dashed p-5 text-left h-auto hover:border-primary hover:bg-primary/5 justify-start"
					onClick={onBlank}
				>
					<div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted">
						<Code className="h-6 w-6 text-muted-foreground" />
					</div>
					<div className="flex-1 min-w-0">
						<p className="font-medium">{t("blank_project")}</p>
						<p className="text-xs text-muted-foreground mt-0.5">
							{t("blank_from_scratch_desc")}
						</p>
					</div>
					<ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
				</Button>
			</div>
		</div>
	)
}

// ── Template Card ──

function TemplateCard({
	template,
	onSelect,
	showTags,
}: {
	template: {
		id: string;
		name: string;
		description: string;
		icon: string;
		tags?: string[];
	}
	onSelect: () => void;
	showTags?: boolean;
}) {
	const { t } = useTranslation("portals");
	const Icon = TEMPLATE_ICONS[template.icon] ?? TEMPLATE_ICONS.default;

	return (
		<Button
			variant="outline"
			className="flex items-center gap-4 rounded-xl p-5 text-left h-auto hover:border-primary hover:shadow-md hover:bg-primary/5 group justify-start"
			onClick={onSelect}
		>
			<div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 shrink-0">
				<Icon className="h-6 w-6 text-primary" />
			</div>
			<div className="flex-1 min-w-0">
				<p className="font-medium">{template.name}</p>
				<p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
					{template.description || t("template_fallback_desc")}
				</p>
				{showTags && template.tags && template.tags.length > 0 && (
					<div className="flex gap-1 mt-2">
						{template.tags.slice(0, 3).map((tag) => (
							<span
								key={tag}
								className="text-[10px] px-1.5 py-0 rounded-full bg-muted text-muted-foreground"
							>
								{tag}
							</span>
						))}
					</div>
				)}
			</div>
			<ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
		</Button>
	)
}

// ── Portal Card (polished) ──

function PortalCard({
	portal,
	navigate,
	index = 0,
}: {
	portal: {
		id: string;
		name: string;
		slug: string;
		status: string;
		activeSessionId: string;
		storeUrl: string;
		updatedAt?: { seconds: bigint };
		publishedAt?: { seconds: bigint };
	}
	navigate: ReturnType<typeof useNavigate>;
	index?: number;
}) {
	const { t } = useTranslation("portals");
	const hasPreview = !!portal.activeSessionId;
	const previewUrl = hasPreview
		? getPreviewUrl(portal.activeSessionId)
		: null;
	const cfg = STATUS_CONFIG[portal.status] ?? STATUS_CONFIG.draft;

	const updatedLabel = portal.updatedAt
		? new Date(Number(portal.updatedAt.seconds) * 1000).toLocaleDateString(
				undefined,
				{ day: "numeric", month: "short" },
			)
		: null;

	return (
		<div
			className="group relative flex flex-col rounded-xl border bg-card overflow-hidden transition-all hover:shadow-lg hover:border-primary/30 cursor-pointer animate-in fade-in slide-in-from-bottom-2 duration-300 fill-mode-both"
			style={{ animationDelay: `${index * 75}ms` }}
			onClick={() =>
				navigate({
					to: "/portals/$id",
					params: { id: portal.id },
				})
			}
			role="button"
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === "Enter")
					navigate({
						to: "/portals/$id",
						params: { id: portal.id },
					})
			}}
		>
			{/* Preview */}
			<div className="relative h-[220px] bg-gradient-to-br from-muted/50 to-muted/20 overflow-hidden">
				{previewUrl ? (
					<iframe
						src={previewUrl}
						title={portal.name}
						className="w-full h-full pointer-events-none scale-[0.5] origin-top-left"
						style={{ width: "200%", height: "200%" }}
						sandbox="allow-scripts allow-same-origin"
						loading="lazy"
					/>
				) : (
					<div className="flex items-center justify-center h-full">
						<div className="text-center">
							<Globe className="h-10 w-10 text-muted-foreground/20 mx-auto" />
							<p className="text-xs text-muted-foreground/40 mt-2">
								{t("preview_unavailable")}
							</p>
						</div>
					</div>
				)}

				{/* Hover overlay */}
				<div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />

				{/* Status */}
				<div className="absolute top-3 left-3">
					<Badge variant={cfg.variant} className="backdrop-blur-sm">
						{t(cfg.label)}
					</Badge>
				</div>

				{/* Quick action on hover */}
				<div
					className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity"
					onClick={(e) => e.stopPropagation()}
				>
					<Button
						size="sm"
						className="shadow-lg gap-1.5 h-8"
						onClick={() =>
							navigate({
								to: "/portals/$id/build",
								params: { id: portal.id },
							})
						}
					>
						<Zap className="h-3 w-3" />
						{t("open_editor")}
					</Button>
				</div>
			</div>

			{/* Body */}
			<div className="p-4 space-y-1">
				<h3 className="font-semibold truncate">{portal.name}</h3>
				<div className="flex items-center gap-2 text-xs text-muted-foreground">
					<span>/{portal.slug}</span>
					{updatedLabel && (
						<>
							<span className="text-muted-foreground/30">·</span>
							<span>{updatedLabel}</span>
						</>
					)}
					{portal.storeUrl && (
						<>
							<span className="text-muted-foreground/30">·</span>
							<a
								href={portal.storeUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="hover:text-foreground transition-colors inline-flex items-center gap-0.5"
								onClick={(e) => e.stopPropagation()}
							>
								{new URL(portal.storeUrl).hostname}
								<ExternalLink className="h-2.5 w-2.5" />
							</a>
						</>
					)}
				</div>
			</div>
		</div>
	)
}

// ── Create Details Dialog (Step 2) ──

function CreateDetailsDialog({
	open,
	templateId,
	templateName,
	onBack,
	onClose,
	onCreated,
}: {
	open: boolean;
	templateId: string;
	templateName?: string;
	onBack: () => void;
	onClose: () => void;
	onCreated: (id: string) => void;
}) {
	const { t } = useTranslation("portals");
	const [name, setName] = useState("");
	const [slug, setSlug] = useState("");
	const [portalType, setPortalType] = useState("storefront");

	const derivedSlug = useMemo(() => {
		if (slug) return slug;
		return name
			.toLowerCase()
			.replace(/[^a-zа-яё0-9]+/gi, "-")
			.replace(/^-|-$/g, "");
	}, [name, slug]);

	const mutation = useMutation({
		mutationFn: async () => {
			return storeClient.createPortalInstance({
				name,
				slug: derivedSlug,
				templateId,
				autoStartAgent: true,
				portalTypeId: portalType,
			})
		},
		onSuccess: (res) => {
			setName("");
			setSlug("");
			onCreated(res.portalInstance?.id ?? "");
		},
	})

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>{t("new_site")}</DialogTitle>
				</DialogHeader>

				<div className="space-y-4">
					{templateName && (
						<div className="flex items-center gap-2 rounded-lg bg-primary/5 border border-primary/10 px-3 py-2">
							<Check className="h-4 w-4 text-primary shrink-0" />
							<span className="text-sm">
								{t("template_label")} <strong>{templateName}</strong>
							</span>
							<Button
								variant="link"
								size="sm"
								className="ml-auto text-xs text-muted-foreground hover:text-foreground"
								onClick={onBack}
							>
								{t("change")}
							</Button>
						</div>
					)}

					<div>
						<Label>{t("name_label")}</Label>
						<Input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder={t("name_placeholder")}
							autoFocus
						/>
					</div>

					<div>
						<Label>{t("portal_type_label")}</Label>
						<select
							value={portalType}
							onChange={(e) => setPortalType(e.target.value)}
							className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
						>
							<option value="storefront">{t("portal_type_storefront")}</option>
							<option value="supplier">{t("portal_type_supplier")}</option>
							<option value="customer">{t("portal_type_customer")}</option>
							<option value="diagnostics">{t("portal_type_diagnostics")}</option>
							<option value="investor">{t("portal_type_investor")}</option>
						</select>
					</div>

					<div>
						<Label>{t("address_label")}</Label>
						<div className="flex items-center gap-0 rounded-md border bg-muted/30 overflow-hidden focus-within:ring-1 focus-within:ring-ring">
							<span className="px-3 text-sm text-muted-foreground bg-muted/50 border-r h-9 flex items-center shrink-0">
								/
							</span>
							<Input
								value={slug}
								onChange={(e) => setSlug(e.target.value)}
								placeholder={derivedSlug || t("slug_placeholder")}
								className="flex-1 bg-transparent px-3 py-2 text-sm outline-none border-0 shadow-none focus-visible:ring-0"
							/>
						</div>
					</div>
				</div>

				<DialogFooter className="gap-2">
					<Button variant="ghost" onClick={onBack}>
						{t("back")}
					</Button>
					<Button
						onClick={() => mutation.mutate()}
						disabled={!name || mutation.isPending}
						className="gap-2"
					>
						{mutation.isPending ? (
							t("creating")
						) : (
							<>
								<Rocket className="h-4 w-4" />
								{t("create_and_start")}
							</>
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
